"use client";

// Mounts the Google Ads gtag and Meta Pixel scripts once the user grants
// Cookiebot marketing consent. Once mounted, the scripts stay mounted for the
// session — re-revoking consent doesn't unload them but our pixel helpers
// (lib/pixels.ts) gate on current consent before firing, so no events are
// dispatched after revocation.

import { Suspense, useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import Script from "next/script";
import { COOKIEBOT_ENABLED } from "@/lib/cookiebot";
import {
  GOOGLE_ADS_ID,
  META_PIXEL_ID,
  hasMarketingConsent
} from "@/lib/pixels";

function currentlyConsented(): boolean {
  if (typeof window === "undefined") return false;
  if (!COOKIEBOT_ENABLED) return true;
  return Boolean(window.Cookiebot?.consent?.marketing);
}

// Fires a PageView on every client-side navigation after the one that mounted
// the pixel.
//
// The init snippet below tracks a single PageView when it runs, and an App
// Router navigation never re-runs it — so without this the site reported one
// PageView per hard page load and Meta saw a fraction of real traffic. The page
// this component mounts on is skipped because the snippet already covered it;
// counting it here too would double it. Unlike PostHogProvider's
// PageviewTracker, which owns the first pageview outright (posthog.init runs
// with `capture_pageview: false`), this one must cede it to the snippet.
function PixelPageViews() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // The URL already counted — by the init snippet on the first run, by us
  // afterwards. Keyed on the URL rather than a "have we run yet" boolean so a
  // repeat run for a page already counted can't emit a second PageView: React
  // Strict Mode double-invokes this effect in dev, which a boolean latch reads
  // as a real navigation.
  const trackedUrl = useRef<string | null>(null);

  useEffect(() => {
    const url = `${pathname}?${searchParams?.toString() ?? ""}`;
    if (trackedUrl.current === url) return;
    const isMountUrl = trackedUrl.current === null;
    trackedUrl.current = url;
    if (isMountUrl) return; // the init snippet tracked this one
    // Re-check consent rather than trusting the mount-time latch, so a
    // mid-session revocation stops further events.
    if (!hasMarketingConsent()) return;
    if (typeof window.fbq !== "function") return;
    window.fbq("track", "PageView");
  }, [pathname, searchParams]);

  return null;
}

export function AdPixels() {
  // One-way latch: once true, never goes back to false. The scripts stay
  // loaded for the rest of the session even if consent is revoked.
  const [hasConsented, setHasConsented] = useState(false);

  useEffect(() => {
    if (currentlyConsented()) setHasConsented(true);

    if (!COOKIEBOT_ENABLED) return;

    const onChange = () => {
      if (currentlyConsented()) setHasConsented(true);
    };

    window.addEventListener("CookiebotOnAccept", onChange);
    window.addEventListener("CookiebotOnConsentReady", onChange);
    return () => {
      window.removeEventListener("CookiebotOnAccept", onChange);
      window.removeEventListener("CookiebotOnConsentReady", onChange);
    };
  }, []);

  if (!hasConsented) return null;

  return (
    <>
      {GOOGLE_ADS_ID && (
        <>
          <Script
            id="google-ads-gtag"
            src={`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`}
            strategy="afterInteractive"
          />
          <Script id="google-ads-init" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              window.gtag = gtag;
              gtag('js', new Date());
              gtag('config', '${GOOGLE_ADS_ID}');
            `}
          </Script>
        </>
      )}

      {META_PIXEL_ID && (
        <>
          <Script id="meta-pixel" strategy="afterInteractive">
            {`
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${META_PIXEL_ID}');
            fbq('track', 'PageView');
          `}
          </Script>
          {/* useSearchParams needs a Suspense boundary under static export. */}
          <Suspense fallback={null}>
            <PixelPageViews />
          </Suspense>
        </>
      )}
    </>
  );
}
